import { api } from '@/lib/api';
import type { MedicalRecord } from '@/features/medical-record/types';
import type {
  Allergy,
  Breed,
  Condition,
  CreateAllergyRequest,
  CreateConditionRequest,
  CreateVaccinationRequest,
  Pet,
  PetPhoto,
  PetSpecies,
  PetWriteRequest,
  Vaccination,
} from './types';

export async function fetch_pets(): Promise<Pet[]> {
  const res = await api.get<Pet[]>('/user/pets');
  return res.data;
}

export async function fetch_pet(id: string): Promise<Pet> {
  const res = await api.get<Pet>(`/user/pets/${id}`);
  return res.data;
}

export async function create_pet(input: PetWriteRequest): Promise<Pet> {
  const res = await api.post<Pet>('/user/pets', input);
  return res.data;
}

export async function update_pet(id: string, input: PetWriteRequest): Promise<Pet> {
  const res = await api.patch<Pet>(`/user/pets/${id}`, input);
  return res.data;
}

export async function delete_pet(id: string): Promise<void> {
  await api.delete(`/user/pets/${id}`);
}

export async function fetch_breeds(species?: PetSpecies): Promise<Breed[]> {
  const res = await api.get<Breed[]>('/lookups/breeds', {
    params: species ? { species } : undefined,
  });
  return res.data;
}

// ---- Photos ----
export async function fetch_pet_photos(pet_id: string): Promise<PetPhoto[]> {
  const res = await api.get<PetPhoto[]>(`/user/pets/${pet_id}/photos`);
  return res.data;
}

export async function upload_pet_photo(pet_id: string, file: File): Promise<PetPhoto> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post<PetPhoto>(`/user/pets/${pet_id}/photos`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

export async function set_primary_pet_photo(pet_id: string, photo_id: string): Promise<void> {
  await api.post(`/user/pets/${pet_id}/photos/${photo_id}/primary`);
}

export async function delete_pet_photo(pet_id: string, photo_id: string): Promise<void> {
  await api.delete(`/user/pets/${pet_id}/photos/${photo_id}`);
}

// ---- Medical records ----
export async function fetch_vaccinations(pet_id: string): Promise<Vaccination[]> {
  return (await api.get<Vaccination[]>(`/user/pets/${pet_id}/vaccinations`)).data;
}
export async function add_vaccination(pet_id: string, input: CreateVaccinationRequest): Promise<Vaccination> {
  return (await api.post<Vaccination>(`/user/pets/${pet_id}/vaccinations`, input)).data;
}
export async function delete_vaccination(pet_id: string, record_id: string): Promise<void> {
  await api.delete(`/user/pets/${pet_id}/vaccinations/${record_id}`);
}

export async function fetch_allergies(pet_id: string): Promise<Allergy[]> {
  return (await api.get<Allergy[]>(`/user/pets/${pet_id}/allergies`)).data;
}
export async function add_allergy(pet_id: string, input: CreateAllergyRequest): Promise<Allergy> {
  return (await api.post<Allergy>(`/user/pets/${pet_id}/allergies`, input)).data;
}
export async function delete_allergy(pet_id: string, record_id: string): Promise<void> {
  await api.delete(`/user/pets/${pet_id}/allergies/${record_id}`);
}

// ---- Vet-authored clinical records (read-only, finalized) ----
export async function fetch_pet_medical_records(pet_id: string): Promise<MedicalRecord[]> {
  return (await api.get<MedicalRecord[]>(`/user/pets/${pet_id}/medical-records`)).data;
}

export async function fetch_conditions(pet_id: string): Promise<Condition[]> {
  return (await api.get<Condition[]>(`/user/pets/${pet_id}/conditions`)).data;
}
export async function add_condition(pet_id: string, input: CreateConditionRequest): Promise<Condition> {
  return (await api.post<Condition>(`/user/pets/${pet_id}/conditions`, input)).data;
}
export async function delete_condition(pet_id: string, record_id: string): Promise<void> {
  await api.delete(`/user/pets/${pet_id}/conditions/${record_id}`);
}
